import { type FC, useState, useEffect, useMemo, useRef } from "react";

import { ChevronLeft, Lock, X } from "lucide-react";

import MainLayout from "@/pages/layouts/MainLayout";
import { PageActions } from "@/pages/layouts/components/PageActions";
import { Button } from "@/shared/components/ui/button";
import { IconButton } from "@/shared/components/ui/icon-button";
import { IllustratedDialog } from "@/shared/components/ui/IllustratedDialog";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useHasCourtDocs } from "@/shared/hooks/useHasCourtDocs";
import { useModuleProgress } from "@/shared/hooks/useModuleProgress";
import { PlanCheckout } from "@/shared/components/PlanCheckout";
import { useCheckout } from "@/shared/hooks/useCheckout";
import { usePricingViewTracking } from "@/shared/hooks/usePricingViewTracking";
import { usePaymentTier } from "@/shared/hooks/usePaymentTier";
import { hasMinTier } from "@/shared/utils/tierOrder";
import { useUserSettingsData } from "@/shared/hooks/useUserSettingsData";
import {
  ASSET_SEARCH_TASK_ID,
  isAssetSearchInProgress,
  isAssetSearchSubmitted,
} from "@/shared/constants/assetSearch";
import { useSubmitAssetSearch } from "@/shared/hooks/useSubmitAssetSearch";
import { useNotificationFlags } from "@/shared/hooks/useNotificationFlags";
import { notifyAssetSearchStarted, notifyBookingFlowOpened } from "@/shared/utils/slackNotifications";
import { logActivity } from "@/shared/utils/activityLogger";
import { supabase } from "@/shared/lib/supabase";
import { getAuthorityVerificationConfig } from "@/shared/constants/authorityVerification";
import { useAuthorityAnswers, isTrusteeAuthority } from "@/shared/hooks/useAuthorityAnswers";
import { CalendlyModal } from "@/shared/components/overlay/CalendlyModal";
import { useCalendlyBooking } from "@/shared/hooks/useCalendlyBooking";
import { useSaveCalendlyBooking } from "@/shared/hooks/useSaveCalendlyBooking";
import { CALENDLY_URLS, ASSET_SEARCH_BOOKING_SOURCE } from "@/shared/constants/calendlyUrls";
import { useProbateAnswers } from "@/shared/hooks/useWillAnswer";

import { TPASignatureModal } from "@/shared/components/TPASignatureModal";

import { AssetSearchVerificationModal } from "./components/AssetSearchVerificationModal";
import { HeroBanner, AuthorityCopySkeleton } from "./components/HeroBanner";
import { SearchCategoryCard } from "./components/SearchCategoryCard";
import { ScheduleCallDialog } from "./components/ScheduleCallDialog";
import { SEARCH_CATEGORIES } from "./searchCategories";

import verifyAndSignImg from "@/shared/assets/illustrations/verify-and-sign.svg";

// ELA-1690: Flip to false to restore the full 12-screen flow
const SIMPLIFIED_FLOW = true;

export const FindAssetsPage: FC = () => {
  const { person, user } = useAppContext();
  const { mutateAsync: submitAssetSearch } = useSubmitAssetSearch();
  const { canSendNotification } = useNotificationFlags();
  const firstName = person?.first_name ?? "your loved one";
  const { hasCourtDocs, isLoading: isCourtDocsLoading } = useHasCourtDocs(person?.estate_id);
  const isAuthorized = hasCourtDocs;
  const { tier, isLoading: isTierLoading } = usePaymentTier(person?.estate_id);
  const isPaidUser = hasMinTier(tier, "premium");
  const [identityDialogOpen, setIdentityDialogOpen] = useState(false);
  const [verificationOpen, setVerificationOpen] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showTPA, setShowTPA] = useState(false);
  const [calendlyOpen, setCalendlyOpen] = useState(false);
  const [scheduleCallOpen, setScheduleCallOpen] = useState(false);
  const { mutate: saveCalendlyBooking } = useSaveCalendlyBooking(
    person?.estate_id,
    ASSET_SEARCH_BOOKING_SOURCE,
  );
  const { booking: assetSearchBooking, isLoading: isBookingLoading } = useCalendlyBooking(
    isPaidUser ? person?.estate_id : undefined,
    ASSET_SEARCH_BOOKING_SOURCE,
  );
  const { startCheckout } = useCheckout({
    returnUrl: `${window.location.origin}/find-assets?checkout=success`,
    cancelUrl: `${window.location.origin}/find-assets`,
    context: "ASSET_SEARCH_CHECKOUT",
    promptLocation: "find_assets",
    fromTier: tier,
  });
  const { trackDismissed: trackCheckoutDismissed } = usePricingViewTracking({
    isOpen: showCheckout,
    promptLocation: "find_assets",
    currentTier: tier,
    estateId: person?.estate_id,
  });
  const { completedAuthorization } = useUserSettingsData();
  const {
    taskStatusMap,
    taskStartedAtMap,
    isLoading: isModuleProgressLoading,
  } = useModuleProgress();
  const {
    hasWill,
    probateState,
    isLoading: isProbateLoading,
  } = useProbateAnswers(person?.estate_id);
  const { savedAnswers: authorityAnswers, isFetched: areAuthorityAnswersFetched } =
    useAuthorityAnswers(person?.estate_id);
  const isTrustee = isTrusteeAuthority(authorityAnswers);
  const verificationConfig = useMemo(
    () =>
      getAuthorityVerificationConfig({
        isTrustee,
        probateState,
        hasWill,
        personFirstName: firstName,
      }),
    [isTrustee, probateState, hasWill, firstName],
  );
  const isVerificationConfigReady =
    !person?.estate_id || (areAuthorityAnswersFetched && !isProbateLoading);

  const isSearching = isAssetSearchInProgress(taskStatusMap.get(ASSET_SEARCH_TASK_ID));
  const isSubmitted = isAssetSearchSubmitted(taskStatusMap.get(ASSET_SEARCH_TASK_ID));
  const isHeroLoading =
    isCourtDocsLoading ||
    isTierLoading ||
    isModuleProgressLoading ||
    isProbateLoading ||
    isBookingLoading ||
    !areAuthorityAnswersFetched;

  const searchStartedAt = taskStartedAtMap.get(ASSET_SEARCH_TASK_ID) ?? null;

  const submitSearchAndNotify = async () => {
    if (isSubmitted) return;
    await submitAssetSearch();
    const estateId = person?.estate_id;
    if (!estateId) return;
    const authEmail = (await supabase.auth.getUser()).data.user?.email;
    if (canSendNotification(authEmail)) {
      notifyAssetSearchStarted(estateId).catch(() => {});
    }
    if (user?.user_id) {
      logActivity({
        estate_id: estateId,
        action_type: "submitted_task",
        user_id: user.user_id,
        entity_type: "task",
        entity_id: ASSET_SEARCH_TASK_ID,
        metadata: { task_name: "Asset Search" },
      }).catch(() => {});
    }
  };

  const slotSelectedRef = useRef(false);
  const hasHandledPostCheckout = useRef(false);
  useEffect(() => {
    if (hasHandledPostCheckout.current || isTierLoading) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success") {
      hasHandledPostCheckout.current = true;
      params.delete("checkout");
      const cleanUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);

      if (SIMPLIFIED_FLOW && isPaidUser) {
        setScheduleCallOpen(true);
      } else if (!SIMPLIFIED_FLOW && !completedAuthorization) {
        setShowTPA(true);
      }
    }
  }, [completedAuthorization, isTierLoading, isPaidUser]);

  const handleStartSearch = () => {
    if (isSubmitted || isHeroLoading) return;

    if (SIMPLIFIED_FLOW) {
      if (!isPaidUser) {
        setShowCheckout(true);
      } else if (!assetSearchBooking) {
        setScheduleCallOpen(true);
      }
    } else if (!isAuthorized) {
      setIdentityDialogOpen(true);
    } else if (!isPaidUser) {
      setShowCheckout(true);
    } else {
      setShowTPA(true);
    }
  };

  const closeCheckout = () => {
    trackCheckoutDismissed();
    setShowCheckout(false);
  };

  if (showCheckout) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto bg-subtle">
        <div className="relative mx-auto flex min-h-full max-w-[1280px] flex-col items-center px-4 py-8 sm:px-10 lg:px-16">
          <div className="absolute left-4 top-8 sm:left-10 lg:left-16">
            <Button variant="outline" size="lg" onClick={closeCheckout}>
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>
          </div>
          <IconButton
            onClick={closeCheckout}
            className="absolute right-4 top-8 sm:right-10 lg:right-16"
            label="Close"
            tooltip={false}
          >
            <X />
          </IconButton>
          <div className="mt-16 w-full sm:mt-12">
            <PlanCheckout
              title="You're one step from Elayne Asset Search"
              subtitle="Choose what to take off your plate. The estate pays, now or later."
              currentTier={tier}
              preselect={["asset_discovery"]}
              onContinue={(selectedAddons) => {
                const selectedTier = selectedAddons.includes("estate_administration")
                  ? "platinum"
                  : "premium";
                startCheckout(selectedTier);
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <MainLayout header="Find assets" actions={<PageActions />}>
      <div className="flex flex-col gap-10">
        <HeroBanner
          isAuthorized={isAuthorized}
          isPaidUser={isPaidUser}
          isSearching={isSearching}
          isLoading={isHeroLoading}
          searchStartedAt={searchStartedAt}
          config={verificationConfig}
          isConfigReady={isVerificationConfigReady}
          simplifiedFlow={SIMPLIFIED_FLOW}
          callBookedAt={assetSearchBooking ? (assetSearchBooking.start_time ?? "booked") : null}
          onStartSearch={handleStartSearch}
          onReschedule={() => setCalendlyOpen(true)}
        />

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-primary">What your search covers</h3>
            {SIMPLIFIED_FLOW && !isPaidUser && !isSearching && (
              <Lock className="h-4 w-4 text-tertiary" />
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {SEARCH_CATEGORIES.map((category) => (
              <SearchCategoryCard
                key={category.title}
                category={category}
                isSearching={!SIMPLIFIED_FLOW && !isHeroLoading && isSearching}
              />
            ))}
          </div>
        </div>
      </div>

      {!SIMPLIFIED_FLOW && (
        <>
          <IllustratedDialog
            open={identityDialogOpen}
            onOpenChange={setIdentityDialogOpen}
            title="One step to unlock Elayne Asset Search"
            description={
              isVerificationConfigReady ? (
                <p>
                  Banks and insurers will only release {firstName}'s records to someone with legal
                  authority over the estate. Your {verificationConfig.documentName},{" "}
                  {verificationConfig.documentDescription}, is the last document you'll need.
                </p>
              ) : (
                <AuthorityCopySkeleton />
              )
            }
            illustration={verifyAndSignImg}
            illustrationAlt="Legal authority verification"
            ctaText="Add document"
            onCtaClick={() => {
              setIdentityDialogOpen(false);
              setVerificationOpen(true);
            }}
          />

          <AssetSearchVerificationModal
            open={verificationOpen}
            onOpenChange={setVerificationOpen}
            estateId={person?.estate_id}
            personFirstName={firstName}
            config={verificationConfig}
            isConfigReady={isVerificationConfigReady}
            isPaidUser={isPaidUser}
            onComplete={() => {
              if (!isPaidUser) {
                setShowCheckout(true);
                return;
              }
              setShowTPA(true);
            }}
          />

          <TPASignatureModal
            open={showTPA}
            onOpenChange={setShowTPA}
            estateId={person?.estate_id}
            onSigned={() => submitSearchAndNotify()}
          />
        </>
      )}

      <ScheduleCallDialog
        open={scheduleCallOpen}
        onOpenChange={setScheduleCallOpen}
        onSchedule={() => setCalendlyOpen(true)}
      />

      <CalendlyModal
        isOpen={calendlyOpen}
        onClose={() => {
          slotSelectedRef.current = false;
          setCalendlyOpen(false);
        }}
        url={CALENDLY_URLS.ASSET_SEARCH_SETUP}
        onDateAndTimeSelected={() => {
          if (slotSelectedRef.current) return;
          slotSelectedRef.current = true;
          notifyBookingFlowOpened();
        }}
        onEventScheduled={(eventUri: string, inviteeUri: string) => {
          saveCalendlyBooking({ eventUri, inviteeUri });
          setCalendlyOpen(false);
        }}
      />
    </MainLayout>
  );
};
